import React, { useEffect, useMemo, useState } from 'react';
import { Mail, MessageSquare, Copy, RotateCcw, Save, Variable, Eye, Send } from 'lucide-react';
import { DEFAULT_TEMPLATES, TEMPLATE_VARIABLES, NotificationTemplate, TemplateChannel } from '../../data/adminMockData';
/**
 * Templates editor for transactional emails and SMS.
 */
export function AdminTemplates() {
  const [templates, setTemplates] = useState<NotificationTemplate[]>(() => {
    const stored = localStorage.getItem('yarbo_store_templates');
    if (stored) {
      return JSON.parse(stored);
    }
    return DEFAULT_TEMPLATES.map(t => ({
      ...t
    }));
  });
  const [selectedId, setSelectedId] = useState<string>(templates[0]?.id ?? '');
  const [channelFilter, setChannelFilter] = useState<'all' | TemplateChannel>('all');
  const [showPreview, setShowPreview] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  useEffect(() => {
    localStorage.setItem('yarbo_store_templates', JSON.stringify(templates));
  }, [templates]);
  const filtered = useMemo(() => templates.filter(t => channelFilter === 'all' || t.channel === channelFilter), [templates, channelFilter]);
  const selected = templates.find(t => t.id === selectedId);
  const updateSelected = (patch: Partial<NotificationTemplate>) => {
    setTemplates(prev => prev.map(t => t.id === selectedId ? {
      ...t,
      ...patch
    } : t));
  };
  const resetSelected = () => {
    const def = DEFAULT_TEMPLATES.find(t => t.id === selectedId);
    if (def) setTemplates(prev => prev.map(t => t.id === selectedId ? {
      ...def
    } : t));
  };
  const handleSave = () => {
    localStorage.setItem('yarbo_store_templates', JSON.stringify(templates));
    setSaveMessage('Template saved successfully');
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const handleSendTest = () => {
    setSaveMessage('Test sent to dev@merem-robotics.test');
    setTimeout(() => setSaveMessage(''), 3000);
  };
  const insertVariable = (v: string) => {
    if (!selected) return;
    const ta = document.getElementById('tpl-body') as HTMLTextAreaElement | null;
    if (!ta) {
      updateSelected({
        body: selected.body + v
      });
      return;
    }
    const start = ta.selectionStart ?? selected.body.length;
    const end = ta.selectionEnd ?? selected.body.length;
    const next = selected.body.slice(0, start) + v + selected.body.slice(end);
    updateSelected({
      body: next
    });
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + v.length;
      ta.setSelectionRange(pos, pos);
    });
  };
  const sampleData: Record<string, string> = {
    '{{customer.firstName}}': 'Daniel',
    '{{customer.lastName}}': 'Whitlock',
    '{{customer.email}}': 'info@meremrobotics.com',
    '{{customer.phone}}': '+1 (313) 555-0118',
    '{{order.id}}': 'MR-10458',
    '{{order.total}}': '$7,980',
    '{{order.lineItems}}': '• Yarbo Lawn Mower Pro + Snow Blower + Blower × 1 — $7,499\n• YarboCare 3-Year AD + 3-Year EW — $649',
    '{{shipping.address}}': '482 Maple Ridge Dr, Ann Arbor, MI 48104',
    '{{shipping.carrier}}': 'FedEx Freight',
    '{{shipping.trackingNumber}}': '1Z999AA10123456784',
    '{{shipping.trackingShortUrl}}': 'mer.bo/t/abc123',
    '{{shipping.eta}}': 'Mon, Jun 3',
    '{{delivery.date}}': 'Tue, Jun 4',
    '{{refund.amount}}': '$4,799',
    '{{yarbocare.planName}}': '3-Year AD + 3-Year EW',
    '{{yarbocare.startDate}}': 'Jun 4, 2026',
    '{{yarbocare.endDate}}': 'Jun 4, 2031',
    // New variables
    '{{customer_first_name}}': 'Daniel',
    '{{customer_last_name}}': 'Whitlock',
    '{{customer_email}}': 'info@meremrobotics.com',
    '{{order_number}}': 'MR-10458',
    '{{order_total}}': '$7,980',
    '{{order_status}}': 'Paid',
    '{{shipping_address}}': '482 Maple Ridge Dr, Ann Arbor, MI 48104',
    '{{tracking_number}}': '1Z999AA10123456784',
    '{{product_names}}': 'Yarbo Lawn Mower Pro + Snow Blower + Blower',
    '{{discount_code}}': 'SAVE100',
    '{{yarbocare_plan}}': '3-Year AD + 3-Year EW',
    '{{store_name}}': 'Merem Robotics',
    '{{support_email}}': 'support@meremrobotics.com',
    '{{support_phone}}': '1-800-555-0199'
  };
  const renderPreview = (text: string) => Object.entries(sampleData).reduce((acc, [k, v]) => acc.split(k).join(v), text);
  return <div data-id="element-935">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5" data-id="element-936">
        <div data-id="element-937">
          <h1 className="text-2xl font-extrabold text-yarbo-dark mb-1" data-id="element-938">
            Templates
          </h1>
          <p className="text-sm text-gray-600" data-id="element-939">
            Email and SMS messages fired by order events.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-940">
          <div className="inline-flex bg-gray-100 rounded-lg p-1" data-id="element-941">
            {[{
            v: 'all',
            label: 'All'
          }, {
            v: 'email',
            label: 'Email'
          }, {
            v: 'sms',
            label: 'SMS'
          }].map(opt => <button key={opt.v} onClick={() => setChannelFilter(opt.v as any)} className={`px-3 py-1.5 text-xs font-bold rounded-md ${channelFilter === opt.v ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600'}`} data-id="element-942">
                {opt.label}
              </button>)}
          </div>
          <button onClick={() => setShowPreview(p => !p)} className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg ${showPreview ? 'bg-yarbo-dark text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400'}`} data-id="element-943">
            <Eye className="w-4 h-4" data-id="element-944" /> Preview
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-id="element-945">
        {/* List */}
        <aside className="lg:col-span-4 bg-white border border-gray-200 rounded-xl overflow-hidden self-start" data-id="element-946">
          <h2 className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-500 border-b border-gray-200" data-id="element-947">
            Notifications
          </h2>
          <ul className="divide-y divide-gray-100 max-h-[640px] overflow-y-auto" data-id="element-948">
            {filtered.map(t => {
            const Icon = t.channel === 'email' ? Mail : MessageSquare;
            const isActive = t.id === selectedId;
            return <li key={t.id} data-id="element-949">
                  <button onClick={() => setSelectedId(t.id)} className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${isActive ? 'bg-yarbo-yellow/10' : 'hover:bg-gray-50'}`} data-id="element-950">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.channel === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-951">
                      <Icon className="w-4 h-4" data-id="element-952" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-953">
                      <div className="text-sm font-bold text-yarbo-dark leading-tight truncate" data-id="element-954">
                        {t.triggerLabel}
                      </div>
                      <div className="text-[11px] text-gray-500 uppercase tracking-wider" data-id="element-955">
                        {t.channel}
                      </div>
                    </div>
                    <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 flex-shrink-0 ${t.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-956">
                      {t.active ? 'On' : 'Off'}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </aside>

        {/* Editor */}
        <section className="lg:col-span-8" data-id="element-957">
          {!selected ? <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-500" data-id="element-958">
              Select a template to edit
            </div> : <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col h-[640px]" data-id="element-959">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-960">
                <div className="flex items-center gap-3" data-id="element-961">
                  <h2 className="font-bold text-gray-900" data-id="element-962">
                    {selected.triggerLabel}
                  </h2>
                  <span className={`text-[10px] font-bold uppercase tracking-wider rounded-md px-2 py-1 ${selected.channel === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-963">
                    {selected.channel}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-964">
                  {saveMessage && <span className="text-xs font-bold text-green-600 mr-2" data-id="element-965">
                      {saveMessage}
                    </span>}
                  <button onClick={handleSendTest} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-966">
                    <Send className="w-3.5 h-3.5" data-id="element-967" /> Test
                  </button>
                  <button onClick={resetSelected} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-968">
                    <RotateCcw className="w-3.5 h-3.5" data-id="element-969" /> Reset
                  </button>
                  <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-yarbo-dark rounded-md hover:bg-black" data-id="element-970">
                    <Save className="w-3.5 h-3.5" data-id="element-971" /> Save
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4" data-id="element-972">
                {selected.channel === 'email' && <div data-id="element-973">
                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-974">
                      Subject
                    </label>
                    <input type="text" value={selected.subject || ''} onChange={e => updateSelected({
                subject: e.target.value
              })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-975" />
                  </div>}

                <div data-id="element-976">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-977">
                    Body
                  </label>
                  <textarea id="tpl-body" value={selected.body} onChange={e => updateSelected({
                body: e.target.value
              })} rows={selected.channel === 'sms' ? 4 : 14} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" data-id="element-978" />
                  {selected.channel === 'sms' && <p className="text-[11px] text-gray-500 mt-1" data-id="element-979">
                      {selected.body.length} characters · keep under 160 for
                      single SMS segment
                    </p>}
                </div>

                {/* Variables */}
                <div data-id="element-980">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-981">
                    <Variable className="w-3.5 h-3.5" data-id="element-982" /> Insert Variable
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-id="element-983">
                    {TEMPLATE_VARIABLES.map(v => <button key={v} type="button" onClick={() => insertVariable(v)} className="text-[11px] font-mono bg-gray-100 hover:bg-yarbo-yellow text-gray-800 hover:text-yarbo-dark px-2 py-1 rounded inline-flex items-center gap-1 transition-colors" data-id="element-984">
                        <Copy className="w-3 h-3" data-id="element-985" /> {v}
                      </button>)}
                  </div>
                </div>

                {/* Preview */}
                {showPreview && <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-986">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-987">
                      Sample preview
                    </div>
                    {selected.channel === 'email' && selected.subject && <div className="mb-3 pb-3 border-b border-gray-200" data-id="element-988">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1" data-id="element-989">
                          Subject
                        </div>
                        <div className="text-sm font-bold text-yarbo-dark" data-id="element-990">
                          {renderPreview(selected.subject)}
                        </div>
                      </div>}
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-sans" data-id="element-991">
                      {renderPreview(selected.body)}
                    </pre>
                  </div>}
              </div>
            </div>}
        </section>
      </div>
    </div>;
}